import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useAuth } from './AuthContext';
import {
  fetchPlatformMe,
  platformLogin,
  platformLogout,
  platformRefreshSession,
  platformVerify2fa,
  readPlatformSession,
  writePlatformSession,
} from '../services/customerIdentityApi';
import { broadcastLogout, onLogoutBroadcast, attemptCloseTab } from '../utils/crossTabLogout';

const LOGOUT_SCOPE = 'ci-admin-platform';

const CiAdminAuthContext = createContext(null);

const PLATFORM_MODE = 'platform';
const ACCOUNT_MODE = 'account';

const buildPrincipalFromMe = (me) => ({
  userId: me?.user_id || null,
  accountId: me?.account_id || null,
  email: me?.email || '',
  role: me?.role || 'viewer',
  scope: me?.scope || 'sites',
  accessibleSiteIds: Array.isArray(me?.accessible_site_ids)
    ? me.accessible_site_ids.map(String)
    : [],
  accessibleCustomerIds: Array.isArray(me?.accessible_customer_ids)
    ? me.accessible_customer_ids.map(String)
    : [],
  is2faVerified: Boolean(me?.is_2fa_verified),
});

const buildPrincipalFromLoginUser = (user) => ({
  userId: user?.id || null,
  accountId: null,
  email: user?.email || '',
  role: user?.role || 'viewer',
  scope: user?.scope || 'sites',
  accessibleSiteIds: Array.isArray(user?.accessible_site_ids)
    ? user.accessible_site_ids.map(String)
    : [],
  accessibleCustomerIds: Array.isArray(user?.accessible_customer_ids)
    ? user.accessible_customer_ids.map(String)
    : [],
  is2faVerified: true,
});

const buildAccountModePrincipal = (mainAuth) => {
  const acc = mainAuth?.account || {};
  const accountId = acc.accountId ?? null;
  const userId = acc.accountUserId
    ? `main-app:${acc.accountUserId}`
    : 'main-app';
  return {
    userId,
    accountId,
    email: acc.name || (accountId ? `account-${accountId}` : 'main-app user'),
    role: 'super_admin',
    scope: 'all',
    accessibleSiteIds: [],
    accessibleCustomerIds: [],
    is2faVerified: true,
  };
};

const persistSession = ({ tokens }) => {
  const access = tokens?.access_token;
  const refresh = tokens?.refresh_token;
  const expires = tokens?.access_expires_at || tokens?.expires_at || null;
  const user = tokens?.user || null;
  if (!access) return null;
  const session = {
    accessToken: access,
    refreshToken: refresh || null,
    accessExpiresAt: expires,
    user,
  };
  writePlatformSession(session);
  return session;
};

/**
 * Provides the active CI admin principal and the helpers each access mode
 * needs.
 *
 * - `mode="platform"` (mounted under `/cia/*`): the user signs in with a
 *   platform-user JWT. Per-site enforcement applies; logout / refresh /
 *   `/me` hydration all run.
 * - `mode="account"` (mounted under `/customer-identity/*`): the user is
 *   already authenticated by the host SPA's OAuth/API-key. We synthesize a
 *   super-admin principal so the same pages can render with full access,
 *   and the API client falls back to main-app credentials.
 */
export function CiAdminAuthProvider({ children, mode = PLATFORM_MODE }) {
  if (mode === ACCOUNT_MODE) {
    return <AccountModeProvider>{children}</AccountModeProvider>;
  }
  return <PlatformModeProvider>{children}</PlatformModeProvider>;
}

function AccountModeProvider({ children }) {
  const mainAuth = useAuth();

  const value = useMemo(() => {
    const principal = buildAccountModePrincipal(mainAuth);
    return {
      mode: ACCOUNT_MODE,
      session: null,
      principal,
      loading: false,
      error: null,
      isAuthenticated: true,
      isSuperAdmin: true,
      login: async () => {
        throw new Error('login_not_supported_in_account_mode');
      },
      verify2fa: async () => {
        throw new Error('login_not_supported_in_account_mode');
      },
      logout: async () => {},
      refreshPrincipal: async () => principal,
      refreshAccessToken: async () => false,
    };
  }, [mainAuth]);

  return (
    <CiAdminAuthContext.Provider value={value}>{children}</CiAdminAuthContext.Provider>
  );
}

function PlatformModeProvider({ children }) {
  const [session, setSession] = useState(() => readPlatformSession());
  const [principal, setPrincipal] = useState(() => {
    const stored = readPlatformSession();
    return stored?.user ? buildPrincipalFromLoginUser(stored.user) : null;
  });
  const [loading, setLoading] = useState(Boolean(session));
  const [error, setError] = useState(null);

  const clearSession = useCallback(() => {
    writePlatformSession(null);
    setSession(null);
    setPrincipal(null);
  }, []);

  const refreshPrincipal = useCallback(async () => {
    if (!session?.accessToken) {
      setPrincipal(null);
      return null;
    }
    try {
      const me = await fetchPlatformMe();
      const next = buildPrincipalFromMe(me);
      setPrincipal(next);
      return next;
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        clearSession();
      }
      setError(err);
      return null;
    }
  }, [session?.accessToken, clearSession]);

  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!session?.accessToken) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const next = await refreshPrincipal();
      if (cancelled) return;
      setLoading(false);
      if (!next && !principal && session?.user) {
        setPrincipal(buildPrincipalFromLoginUser(session.user));
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.accessToken]);

  const login = useCallback(async ({ email, password }) => {
    setError(null);
    const result = await platformLogin({ email, password });
    if (result?.requires_2fa) {
      return { requires2fa: true, challenge: result.challenge };
    }
    const next = persistSession({ tokens: result?.tokens });
    if (!next) {
      throw new Error('login_failed');
    }
    setSession(next);
    setPrincipal(buildPrincipalFromLoginUser(next.user));
    return { requires2fa: false };
  }, []);

  const verify2fa = useCallback(async (payload) => {
    setError(null);
    const result = await platformVerify2fa(payload);
    const next = persistSession({ tokens: result?.tokens });
    if (!next) {
      throw new Error('login_failed');
    }
    setSession(next);
    setPrincipal(buildPrincipalFromLoginUser(next.user));
    return next;
  }, []);

  const logout = useCallback(async () => {
    if (session?.accessToken) {
      try {
        await platformLogout();
      } catch {
        // Best-effort; clear local state regardless.
      }
    }
    clearSession();
    broadcastLogout(LOGOUT_SCOPE);
  }, [session?.accessToken, clearSession]);

  // Other tabs signed in with the same platform session: clear and close.
  useEffect(() => {
    const unsubscribe = onLogoutBroadcast(LOGOUT_SCOPE, () => {
      clearSession();
      attemptCloseTab();
    });
    return unsubscribe;
  }, [clearSession]);

  const refreshAccessToken = useCallback(async () => {
    if (!session?.refreshToken) return false;
    try {
      const result = await platformRefreshSession(session.refreshToken);
      const next = persistSession({ tokens: result });
      if (!next) return false;
      setSession(next);
      return true;
    } catch {
      clearSession();
      return false;
    }
  }, [session?.refreshToken, clearSession]);

  const value = useMemo(
    () => ({
      mode: PLATFORM_MODE,
      session,
      principal,
      loading,
      error,
      isAuthenticated: Boolean(session?.accessToken && principal),
      isSuperAdmin: principal?.role === 'super_admin',
      login,
      verify2fa,
      logout,
      refreshPrincipal,
      refreshAccessToken,
    }),
    [
      session,
      principal,
      loading,
      error,
      login,
      verify2fa,
      logout,
      refreshPrincipal,
      refreshAccessToken,
    ],
  );

  return (
    <CiAdminAuthContext.Provider value={value}>{children}</CiAdminAuthContext.Provider>
  );
}

export const useCiAdminAuth = () => {
  const ctx = useContext(CiAdminAuthContext);
  if (!ctx) {
    throw new Error('useCiAdminAuth must be used inside <CiAdminAuthProvider>');
  }
  return ctx;
};

/**
 * Returns the route prefix the current CI admin instance is mounted under
 * (`/cia` for platform mode, `/customer-identity` for account mode). Use
 * for building NavLinks, navigates, and breadcrumbs that should keep the
 * caller in their current access mode.
 */
export const useCiAdminBasePath = () => {
  const { mode } = useCiAdminAuth();
  return mode === ACCOUNT_MODE ? '/customer-identity' : '/cia';
};

export default CiAdminAuthContext;
